import { DataTableColumnHeader } from "@/shared/components/table";
import { Button } from "@/shared/components/ui/button";
import type { CollectionErrorRowAPI, CollectionKnownErrorGroupRow } from "@/types/collection-types";
import type { ColumnDef } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { useMemo } from "react";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface UseCollectionNewErrorColumnsOptions {
  // 필요시 옵션 추가
}

export function useCollectionNewErrorColumns(_options: UseCollectionNewErrorColumnsOptions = {}) {
  return useMemo<ColumnDef<CollectionErrorRowAPI>[]>(
    () => [
      {
        accessorKey: "requested_time",
        header: ({ column }) => <DataTableColumnHeader column={column} title="요청 시각" />,
      },
      {
        accessorKey: "trans_id",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Trans ID" />,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">{row.original.trans_id}</span>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" asChild>
              <a
                href={row.original.datadog_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            </Button>
          </div>
        ),
      },
      {
        accessorKey: "channel",
        header: ({ column }) => <DataTableColumnHeader column={column} title="채널" />,
        cell: ({ row }) => row.original.channel || "-",
      },
      {
        accessorKey: "sub_channel",
        header: ({ column }) => <DataTableColumnHeader column={column} title="서브 채널" />,
        cell: ({ row }) => row.original.sub_channel || "-",
      },
      {
        accessorKey: "dw_account_id",
        header: ({ column }) => <DataTableColumnHeader column={column} title="DW Account ID" />,
      },
      {
        accessorKey: "err_msg",
        header: ({ column }) => <DataTableColumnHeader column={column} title="오류 메시지" />,
        cell: ({ row }) => (
          <div className="whitespace-normal break-words">{row.original.err_msg || "-"}</div>
        ),
      },
      {
        accessorKey: "org_name",
        header: ({ column }) => <DataTableColumnHeader column={column} title="조직명" />,
        cell: ({ row }) => row.original.org_name || "-",
      },
      {
        accessorKey: "workspace_ids",
        header: ({ column }) => <DataTableColumnHeader column={column} title="워크스페이스 ID" />,
        cell: ({ row }) =>
          row.original.workspace_ids.length > 0 ? row.original.workspace_ids.join(", ") : "-",
      },
    ],
    []
  );
}

export function useCollectionKnownErrorGroupColumns() {
  return useMemo<ColumnDef<CollectionKnownErrorGroupRow>[]>(
    () => [
      {
        accessorKey: "count",
        header: ({ column }) => <DataTableColumnHeader column={column} title="오류 발생 수" />,
        cell: ({ row }) => (
          <span className="font-semibold text-destructive">{row.original.count}</span>
        ),
      },
      {
        accessorKey: "channel",
        header: ({ column }) => <DataTableColumnHeader column={column} title="채널" />,
      },
      {
        accessorKey: "pattern",
        header: ({ column }) => <DataTableColumnHeader column={column} title="패턴" />,
        cell: ({ row }) => (
          <div className="max-w-lg">
            <code className="rounded bg-muted px-2 py-1 text-xs">{row.original.pattern}</code>
          </div>
        ),
      },
      {
        accessorKey: "description",
        header: ({ column }) => <DataTableColumnHeader column={column} title="설명" />,
        cell: ({ row }) => (
          <div className="max-w-md truncate" title={row.original.description || ""}>
            {row.original.description || "-"}
          </div>
        ),
      },
    ],
    []
  );
}
